
import Card from "@mui/material/Card";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DataTable } from "@maysoft/common-component-react";
import { useNavigate, useSearchParams } from "react-router-dom";

import Helpers from "commons/helpers";
import Screens from "constants/screens";
import Strings from "constants/strings";
import Constants from "constants/index";
import ProjectService from "services/medkit/project.service";
import ModalCreateUpdateProject from "./ModalCreateUpdateProject";

import { RootState } from "store";
import { DashboardLayout } from "layout";
import { IRecordProject } from "commons/interfaces";
import { setListPathName } from "store/slice/titleRoute.slice";
import { setDataAlert, showLoadingDataTable } from "store/slice/common.slice";



interface IDataFilterProject {
    searchText?: string;

    pageSize?: number;
    pageNumber?: number;
    totalCount?: number;
};

const projectService = new ProjectService();

const ProjectManagementScreen = () => {

    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [searchParams] = useSearchParams();

    const loadingDataTable = useSelector((state: RootState) => state.common.loadingDataTable);

    const [dataRows, setDataRows] = useState<IRecordProject[]>([]);
    const [dataFilter, setDataFilter] = useState<IDataFilterProject>({});

    const [openModalCreateUpdate, setOpenModalCreateUpdate] = useState(false);
    const [projectDetail, setProjectDetail] = useState<IRecordProject | undefined>(undefined);

    const pageSize = searchParams.get("size");
    const pageNumber = searchParams.get("page");
    const searchText = searchParams.get("text");

    useEffect(() => {
        const size = Number(pageSize || Constants.ROW_PER_PAGE);
        const page = Number(pageNumber || Constants.PAGE_DEFAULT);


        const newReq: IDataFilterProject = {
            pageSize: size,
            pageNumber: page,
            searchText: searchText,
        };

        loadDataProject(newReq);
    }, [
        pageSize,
        pageNumber,
        searchText,
    ]);

    const loadDataProject = async (newReq: IDataFilterProject) => {
        try {
            dispatch(showLoadingDataTable(true));

            const result = await projectService.getPaged({
                pageSize: newReq.pageSize,
                pageNumber: newReq.pageNumber,
                searchText: newReq.searchText,
            });

            setDataFilter(prev => ({ ...prev, ...newReq, totalCount: result?.totalCount || 0 }));

            setDataRows([...result?.items || []]);

        } catch (error) {
            const err = Helpers.handleExceptionError(error);
            !Helpers.isNullOrEmpty(err) && dispatch(setDataAlert({ message: err, type: "error" }));
        } finally {
            dispatch(showLoadingDataTable(false));
        }
    };

    const handleFilterData = (req: IDataFilterProject) => {
        let query: string = `?page=${req.pageNumber || Constants.PAGE_DEFAULT}&size=${req.pageSize || Constants.ROW_PER_PAGE}`;

        if (!Helpers.isNullOrEmpty(req.searchText)) {
            query += `&text=${req.searchText}`;
        }

        navigate(Screens.PROJECT + query, { replace: true });

        dispatch(setListPathName({ pathname: Screens.PROJECT, query }));
    };

    const handleDelete = (id: string) => {
        Helpers.showConfirmAlert(
            Strings.PROJECT_MANAGEMENT.CONFIRM_DELETE_PROJECT,
            async () => {
                try {
                    dispatch(showLoadingDataTable(true));

                    await projectService.delete(id);

                    await loadDataProject({ ...dataFilter });

                    dispatch(setDataAlert({ message: Strings.PROJECT_MANAGEMENT.DELETE_PROJECT_SUCCESS, type: "success" }));
                } catch (error) {
                    const err = Helpers.handleExceptionError(error);
                    !Helpers.isNullOrEmpty(err) && dispatch(setDataAlert({ message: err, type: "error" }));
                } finally {
                    dispatch(showLoadingDataTable(false));
                }
            },
            () => { },
        );
    };

    const dataColumns = useMemo(() => ([
        { Header: Strings.PROJECT_MANAGEMENT.PROJECT_NAME, accessor: "name" },
        { Header: Strings.PROJECT_MANAGEMENT.DESCRIPTION, accessor: "description" },
    ]), []);

    return (
        <DashboardLayout
            isPermission
            allowSettingLayout
            requiredOrganization
            title={Strings.PROJECT_MANAGEMENT.TITLE}
            route={[{ title: Strings.PROJECT_MANAGEMENT.TITLE, route: "" }]}
            listActions={[
                {
                    title: Strings.COMMON.ADD_NEW,
                    onPress() {
                        setProjectDetail(undefined);
                        setOpenModalCreateUpdate(true);
                    },
                }
            ]}
        >
            <Card sx={{ padding: 2 }}>
                <DataTable
                    isShowIndex
                    loading={loadingDataTable}
                    totalCount={dataFilter?.totalCount || 0}
                    rowPerPage={dataFilter?.pageSize || Constants.ROW_PER_PAGE}
                    pageNumber={dataFilter?.pageNumber || Constants.PAGE_DEFAULT}
                    onChangePageSize={(value) => {
                        handleFilterData({ ...dataFilter, pageSize: value, });
                    }}
                    onChangePageNumber={(value) => {
                        handleFilterData({ ...dataFilter, pageNumber: value, });
                    }}
                    table={{ columns: dataColumns as any, rows: dataRows }}

                    searchText={dataFilter?.searchText}
                    placeholderSearch={Strings.PROJECT_MANAGEMENT.PLACEHOLDER_SEARCH_TEXT}
                    onSearch={(value) => {
                        handleFilterData({
                            ...dataFilter,
                            searchText: value,
                            pageNumber: Constants.PAGE_DEFAULT,
                        });
                    }}
                    onSearchChange={(value) => {
                        setDataFilter(prev => ({
                            ...prev,
                            searchText: value,
                            pageNumber: Constants.PAGE_DEFAULT,
                        }));
                    }}
                    actionList={(row) => ([
                        {
                            key: "view",
                            iconName: "view",
                            title: Strings.PROJECT_MANAGEMENT.VIEW_LIST_ORGANIZATION,
                            callback: (row) => {
                                navigate(Screens.PROJECT_ORGANIZATION + `?project=${row.id}`);
                            },
                        },
                        {
                            key: "edit",
                            iconName: "edit",
                            title: Strings.COMMON.EDIT,
                            callback: (row) => {
                                setProjectDetail(row);
                                setOpenModalCreateUpdate(true);
                            },
                        },
                        {
                            key: "delete",
                            iconName: "delete",
                            title: Strings.COMMON.DELETE,
                            callback: (row) => {
                                handleDelete(row.id);
                            },
                        },
                    ])}
                />
            </Card>

            {openModalCreateUpdate &&
                <ModalCreateUpdateProject
                    projectId={projectDetail?.id}
                    visible={openModalCreateUpdate}
                    setVisible={setOpenModalCreateUpdate}
                    onCallBack={() => {
                        setProjectDetail(undefined);
                        loadDataProject({ ...dataFilter });
                    }}
                />
            }
        </DashboardLayout>
    );
};

export default ProjectManagementScreen;
